import type { Directive } from 'vue';

import { shallowRef } from 'vue';

import type { IContactSummaryContact } from './contact-summary.helpers';

import { ContactSummaries } from './contact-summary';
import { canOpenContactCard } from './contact-summary.access';

/**
 * `v-contact-hover="contact | id | falsy"` — shows the contact summary card after a short mouse
 * hover. Only one card exists globally; the host (c-contact-hover-host.vue) renders whatever is in
 * `activeContactHover`.
 */

export type ContactHoverValue = IContactSummaryContact | string | null | undefined | false;

export interface IActiveContactHover {
    anchorEl: HTMLElement;
    contact: IContactSummaryContact | string;
}

const PREFETCH_DELAY_MS = 150;
const OPEN_DELAY_MS = 450;
const QUICK_OPEN_DELAY_MS = 120;
const QUICK_WINDOW_MS = 400;
const CLOSE_DELAY_MS = 200;

export const activeContactHover = shallowRef<IActiveContactHover | null>(null);

let closeTimer: ReturnType<typeof setTimeout> | null = null;
let lastClosedAt = -Infinity;

export function cancelClose() {
    if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
    }
}

export function closeContactHover() {
    cancelClose();
    if (activeContactHover.value) {
        activeContactHover.value = null;
        lastClosedAt = Date.now();
    }
}

export function scheduleClose() {
    cancelClose();
    closeTimer = setTimeout(closeContactHover, CLOSE_DELAY_MS);
}

interface IHoverState {
    value: ContactHoverValue;
    prefetchTimer: ReturnType<typeof setTimeout> | null;
    openTimer: ReturnType<typeof setTimeout> | null;
    onEnter: (e: Event) => void;
    onLeave: () => void;
    onDown: () => void;
}

const states = new WeakMap<HTMLElement, IHoverState>();

function idOf(value: ContactHoverValue): string | null {
    if (!value) return null;
    return typeof value === 'string' ? value : value.id;
}

function clearOpenTimers(state: IHoverState) {
    if (state.prefetchTimer) clearTimeout(state.prefetchTimer);
    if (state.openTimer) clearTimeout(state.openTimer);
    state.prefetchTimer = null;
    state.openTimer = null;
}

export const vContactHover: Directive<HTMLElement, ContactHoverValue> = {
    mounted(el, binding) {
        const state: IHoverState = {
            value: binding.value,
            prefetchTimer: null,
            openTimer: null,
            onEnter(e) {
                // Mouse only: touch/pen (and the native webview) have no hover.
                if ((e as PointerEvent).pointerType !== 'mouse') return;
                const id = idOf(state.value);
                if (!id || !state.value || !canOpenContactCard(state.value)) return;
                // Back on the open card's own anchor: keep it. Crossing another trigger must not
                // cancel the open card's pending close (its own open timer takes over below).
                if (activeContactHover.value?.anchorEl === el) cancelClose();
                clearOpenTimers(state);
                state.prefetchTimer = setTimeout(() => {
                    state.prefetchTimer = null;
                    ContactSummaries.request([id]);
                }, PREFETCH_DELAY_MS);
                const quick = !!activeContactHover.value || Date.now() - lastClosedAt < QUICK_WINDOW_MS;
                state.openTimer = setTimeout(
                    () => {
                        state.openTimer = null;
                        // Detached (e.g. clicked through to a kept-alive route): never open off-page.
                        if (!state.value || !canOpenContactCard(state.value) || !el.isConnected) return;
                        ContactSummaries.request([id]);
                        cancelClose();
                        activeContactHover.value = { anchorEl: el, contact: state.value };
                    },
                    quick ? QUICK_OPEN_DELAY_MS : OPEN_DELAY_MS
                );
            },
            onLeave() {
                clearOpenTimers(state);
                if (activeContactHover.value) scheduleClose();
            },
            onDown() {
                // A click navigates or opens something else; don't open a card afterwards.
                clearOpenTimers(state);
            }
        };
        states.set(el, state);
        el.addEventListener('pointerenter', state.onEnter);
        el.addEventListener('pointerleave', state.onLeave);
        el.addEventListener('pointerdown', state.onDown);
    },

    updated(el, binding) {
        const state = states.get(el);
        if (!state) return;
        state.value = binding.value;
        if (activeContactHover.value?.anchorEl === el) {
            if (!binding.value) closeContactHover();
            else if (activeContactHover.value.contact !== binding.value) activeContactHover.value = { anchorEl: el, contact: binding.value };
        }
    },

    unmounted(el) {
        const state = states.get(el);
        if (!state) return;
        clearOpenTimers(state);
        el.removeEventListener('pointerenter', state.onEnter);
        el.removeEventListener('pointerleave', state.onLeave);
        el.removeEventListener('pointerdown', state.onDown);
        states.delete(el);
        if (activeContactHover.value?.anchorEl === el) closeContactHover();
    }
};
